import clsx from 'clsx'
import { nothing, unsafeCSS } from 'lit'
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { Alignment, BackgroundShade } from '@/common/types'
import { DdsElement } from '@/internal/dds-hu-element'
import { translate } from '@/localization/localization'

import styles from './callout.scss?inline'

export type CalloutVariant =
  | 'brand'
  | 'info'
  | 'positive'
  | 'warning'
  | 'negative'

/**
 * `dap-ds-callout` is a custom callout component.
 * @element dap-ds-callout
 * @title - Callout
 *
 * @event {{ void }} dds-close - Fired when the close button is clicked.
 *
 * @slot - The content of the callout.
 * @slot title - The title of the callout.
 * @slot icon - The icon of the callout.
 * @slot actions - The actions of the callout.
 * @slot close - The close button of the callout.
 *
 * @csspart base - The main callout container.
 * @csspart icon - The icon of the callout.
 * @csspart title - The title of the callout.
 * @csspart description - The description of the callout.
 * @csspart actions - The actions of the callout.
 * @csspart close - The close button of the callout.
 *
 * @cssproperty --dds-callout-padding - Padding of the callout content. Default: var(--dds-spacing-300)
 * @cssproperty --dds-callout-gap - Gap between elements in horizontal layout. Default: var(--dds-spacing-300)
 * @cssproperty --dds-callout-icon-size - Size of the icon. Default: var(--dds-spacing-600)
 * @cssproperty --dds-callout-title-color - Color of the title text. Default: var(--dds-text-neutral-strong)
 * @cssproperty --dds-callout-description-color - Color of the description text. Default: var(--dds-text-neutral-base)
 * @cssproperty --dds-callout-title-font-size - Font size of the title. Default: var(--dds-font-base)
 * @cssproperty --dds-callout-description-font-size - Font size of the description. Default: var(--dds-font-base)
 * @cssproperty --dds-callout-title-font-weight - Font weight of the title. Default: var(--dds-font-weight-bold)
 * @cssproperty --dds-callout-description-font-weight - Font weight of the description. Default: var(--dds-font-weight-medium)
 * @cssproperty --dds-callout-actions-font-weight - Font weight of the actions. Default: var(--dds-font-weight-bold)
 * @cssproperty --dds-callout-actions-gap - Gap between action items. Default: var(--dds-spacing-400)
 * @cssproperty --dds-callout-content-gap - Gap between content elements. Default: var(--dds-spacing-100)
 * @cssproperty --dds-callout-border-radius - Border radius of the callout. Default: var(--dds-radius-base)
 *
 * @cssproperty --dds-callout-brand-background - Brand variant background color. Default: var(--dds-background-brand-base)
 * @cssproperty --dds-callout-brand-background-subtle - Brand variant subtle background color. Default: var(--dds-background-brand-subtle)
 * @cssproperty --dds-callout-brand-background-medium - Brand variant medium background color. Default: var(--dds-background-brand-medium)
 * @cssproperty --dds-callout-brand-background-strong - Brand variant strong background color. Default: var(--dds-background-brand-strong)
 * @cssproperty --dds-callout-brand-icon - Brand variant icon color. Default: var(--dds-icon-brand-subtle)
 * @cssproperty --dds-callout-brand-border - Brand variant border color. Default: var(--dds-border-brand-subtle)
 *
 * @cssproperty --dds-callout-info-background - Info variant background color. Default: var(--dds-background-informative-base)
 * @cssproperty --dds-callout-info-background-subtle - Info variant subtle background color. Default: var(--dds-background-informative-subtle)
 * @cssproperty --dds-callout-info-background-medium - Info variant medium background color. Default: var(--dds-background-informative-medium)
 * @cssproperty --dds-callout-info-background-strong - Info variant strong background color. Default: var(--dds-background-informative-strong)
 * @cssproperty --dds-callout-info-icon - Info variant icon color. Default: var(--dds-icon-informative-subtle)
 * @cssproperty --dds-callout-info-border - Info variant border color. Default: var(--dds-border-informative-subtle)
 *
 * @cssproperty --dds-callout-positive-background - Positive variant background color. Default: var(--dds-background-positive-base)
 * @cssproperty --dds-callout-positive-background-subtle - Positive variant subtle background color. Default: var(--dds-background-positive-subtle)
 * @cssproperty --dds-callout-positive-background-medium - Positive variant medium background color. Default: var(--dds-background-positive-medium)
 * @cssproperty --dds-callout-positive-background-strong - Positive variant strong background color. Default: var(--dds-background-positive-strong)
 * @cssproperty --dds-callout-positive-icon - Positive variant icon color. Default: var(--dds-icon-positive-subtle)
 * @cssproperty --dds-callout-positive-border - Positive variant border color. Default: var(--dds-border-positive-subtle)
 *
 * @cssproperty --dds-callout-warning-background - Warning variant background color. Default: var(--dds-background-warning-base)
 * @cssproperty --dds-callout-warning-background-subtle - Warning variant subtle background color. Default: var(--dds-background-warning-subtle)
 * @cssproperty --dds-callout-warning-background-medium - Warning variant medium background color. Default: var(--dds-background-warning-medium)
 * @cssproperty --dds-callout-warning-background-strong - Warning variant strong background color. Default: var(--dds-background-warning-strong)
 * @cssproperty --dds-callout-warning-icon - Warning variant icon color. Default: var(--dds-icon-neutral-strong)
 * @cssproperty --dds-callout-warning-border - Warning variant border color. Default: var(--dds-border-warning-subtle)
 *
 * @cssproperty --dds-callout-negative-background - Negative variant background color. Default: var(--dds-background-negative-base)
 * @cssproperty --dds-callout-negative-background-subtle - Negative variant subtle background color. Default: var(--dds-background-negative-subtle)
 * @cssproperty --dds-callout-negative-background-medium - Negative variant medium background color. Default: var(--dds-background-negative-medium)
 * @cssproperty --dds-callout-negative-background-strong - Negative variant strong background color. Default: var(--dds-background-negative-strong)
 * @cssproperty --dds-callout-negative-icon - Negative variant icon color. Default: var(--dds-icon-negative-subtle)
 * @cssproperty --dds-callout-negative-border - Negative variant border color. Default: var(--dds-border-negative-subtle)
 */
@customElement('dap-ds-callout')
export default class DapDSCallout extends DdsElement {
  /** @ignore */
  @queryAssignedElements({ slot: 'actions' }) actionSlot: Element[]

  /** The variant of the callout
   * @type { 'brand' | 'positive' | 'info' | 'warning' | 'negative' }
   */
  @property() variant: CalloutVariant = 'brand'

  /** The alignment of the callout. Can be `vertical` or `horizontal`.
   * @type { 'vertical' | 'horizontal' }
   */
  @property() alignment: Alignment = 'horizontal'

  /** The strongness of the callout. Can be `subtle`, `base`, `medium`, or `strong`.
   * @type { 'subtle' | 'base' | 'medium' | 'strong' }
   */
  @property() shade: BackgroundShade = 'base'

  /** The border of the callout */
  @property({ type: String }) noBorder: string = 'true'

  /** If the callout has a close button */
  @property({ type: Boolean }) closeable: boolean = false

  /** The header of the callout */
  @property() title: string

  /** The label of the close button */
  @property() closeButtonLabel: string

  /** If the callout is opened */
  @property({ reflect: true }) opened: string = 'true'

  static readonly styles = unsafeCSS(styles)

  private get hasActions(): boolean {
    return this.actionSlot && this.actionSlot.length > 0
  }

  private static readonly defaultIcons = {
    brand: html`<dap-ds-icon-information-2-fill
      aria-hidden="true"></dap-ds-icon-information-2-fill>`,
    info: html`<dap-ds-icon-information-2-fill
      aria-hidden="true"></dap-ds-icon-information-2-fill>`,
    positive: html`<dap-ds-icon-checkbox-circle-fill
      aria-hidden="true"></dap-ds-icon-checkbox-circle-fill>`,
    warning: html`<dap-ds-icon-alert-fill
      aria-hidden="true"></dap-ds-icon-alert-fill>`,
    negative: html`<dap-ds-icon-error-warning-fill
      aria-hidden="true"></dap-ds-icon-error-warning-fill>`,
  } as const

  getDefaultIcon(variant: CalloutVariant) {
    return DapDSCallout.defaultIcons[variant] ?? DapDSCallout.defaultIcons.brand
  }

  handleClose() {
    this.emit('dds-close')
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.closeable) {
      event.preventDefault()
      this.handleClose()
    }
  }

  render() {
    return this.opened !== 'false'
      ? html`
          <dap-ds-card
            part="base"
            role="region"
            aria-live="polite"
            aria-labelledby=${this.title ? 'callout-title' : nothing}
            @keydown=${this.handleKeyDown}
            .noBorder=${this.noBorder === 'true'}
            class=${clsx(
              'callout',
              `callout--${this.variant}`,
              `callout--${this.alignment}`,
              `callout--${this.shade}`,
            )}>
            <dap-ds-card-content class="callout__content" spacing="none">
              <span class="callout__icon">
                <slot name="icon" part="icon">
                  ${this.getDefaultIcon(this.variant)}
                </slot>
              </span>
              <span class="callout__content-wrapper">
                ${this.title
                  ? html`<span
                      part="title"
                      class="callout__title"
                      id="callout-title">
                      <slot name="title">${this.title}</slot>
                    </span>`
                  : nothing}
                <span part="description" class="callout__description">
                  <slot></slot>
                </span>
                ${this.hasActions
                  ? html`<span part="actions" class="callout__actions">
                      <slot name="actions" part="actions"></slot>
                    </span>`
                  : nothing}
              </span>
              ${this.closeable
                ? html` <span part="close" class="callout__close">
                    <slot name="close">
                      <dap-ds-icon-button
                        clean
                        aria-label=${this.closeButtonLabel
                          ? this.closeButtonLabel
                          : translate('close')}
                        @click=${this.handleClose}>
                        <dap-ds-icon name="close-line"></dap-ds-icon>
                      </dap-ds-icon-button>
                    </slot>
                  </span>`
                : nothing}
            </dap-ds-card-content>
          </dap-ds-card>
        `
      : nothing
  }
}
